import React, { useContext, useEffect, useMemo, useState } from "react";
import {
    Alert,
    alpha,
  Autocomplete,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  TextField,
  Typography,
} from "@mui/material";
import Decimal from "decimal.js";
import { DeliveryChargeRuleScope, IDeliveryChargeRule } from "./interfaces";
import { governorates, regions } from "../../../../data/regions";
import { AuthContext } from "../../../../contexts/user-context/user-context";
import { useSnackBar } from "../../../../contexts/snackbar";
import { API_ENDPOINT } from "../../../../configurations/global.config";
import axios from "axios";
import { IArea } from "../../../../interfaces/common/location";
import { Add, Delete, Edit } from "@mui/icons-material";
import AutocompleteWithThrottle from "../../../common/autocomplete-with-throttle/autocomplete-with-throttle";
import { MerchantType } from "../../../../interfaces/delivery-company/merchants";
import { green, red } from "@mui/material/colors";
import ConfirmAction from "../../../common/confirm-action/confirm-action";
import { DataResources, ResourceActionType, user_has_data_model_access } from "../../../../utils/permissions";


interface IDeliveryZoneAutocompleteProps {
    rules: IDeliveryChargeRule[]
    focus: IDeliveryChargeRule | undefined
    setFocus: React.Dispatch<React.SetStateAction<IDeliveryChargeRule | undefined>>
    merchant: MerchantType
    refetchRules: ()=>void;
    readonly?:boolean
}

interface IOption  {
    id?: number,
    label: string,
    scope: DeliveryChargeRuleScope,
    name: string,
    rule?: IDeliveryChargeRule
    district?: string,
    governorate?: string,
    area?: IArea
}

const DeliveryZoneAutocomplete: React.FC<IDeliveryZoneAutocompleteProps> = ({rules, focus, setFocus, merchant, refetchRules, readonly=false}) => {
  const [selected, setSelected] = useState<IOption | null>(null);
  const [chargeInput, setChargeInput] = useState("");
  const [dialogOpen, setDialogOpen] = useState<IOption>();
  const {config} = useContext(AuthContext);
  const {showMessage} = useSnackBar();
  const [isSaving, setIsSaving] = useState(false)
  const [confirmingDeleteFor, setConfirmingDeleteFor] = useState<IOption>()
  const [isDeleting, setIsDeleting] = useState(false)
  const { user } = useContext(AuthContext)

  // GET EXISTING RULE
  const getRule = (option: IOption | null): IDeliveryChargeRule | undefined => {
    if (!option) return undefined
    const scoped_rules = rules.filter(item => item.scope == option.scope)
    if (option.scope == DeliveryChargeRuleScope.AREA) return scoped_rules.find(item => item.area?.code == option.area!.code)
    if (option.scope == DeliveryChargeRuleScope.DISTRICT) return scoped_rules.find(item => item.district == option.district)
    if (option.scope == DeliveryChargeRuleScope.GOVERNORATE) return scoped_rules.find(item => item.governorate == option.governorate)
  };

  // SAVE OR EDIT RULE
  const handleSave = async (option: IOption) => {
    if (isNaN(Number(chargeInput))) return;
    setIsSaving(true)
    try {
        const endpoint = `${API_ENDPOINT}/v1/delivery-fee-rules/`
        const requestData :any = {
            scope: option.scope,
            delta_charge: new Decimal(chargeInput),            
            merchant: {id: merchant.id},
        }

        if (option.scope == DeliveryChargeRuleScope.AREA) requestData.area = option.area
        if (option.scope == DeliveryChargeRuleScope.DISTRICT) requestData.district = option.district
        if (option.scope == DeliveryChargeRuleScope.GOVERNORATE) requestData.governorate = option.governorate 
        const result = await axios.post<IDeliveryChargeRule>(endpoint, requestData, config)
        showMessage('Updated successfully')
        console.log(result.data)
        setFocus(result.data)
        const newSelected = {...option, rule: {...result.data, delta_charge: new Decimal(result.data.delta_charge)}}
        setSelected(newSelected)
        newSelected ? setFocus({...newSelected, delta_charge: Decimal(0)}) : setFocus(undefined)
        refetchRules()
        setDialogOpen(undefined);
    } catch {
        showMessage('Something went wrong. Try again or contact us.', 'error')
    } finally {
        setIsSaving(false)
    }
  };

  const handleDelete = async (option: IOption) => {
    if (option.rule && option.rule.id) {
        setIsDeleting(true)
        try {
            await axios.delete(`${API_ENDPOINT}/v1/delivery-fee-rules/${option.rule.id}/`, config)
            showMessage('Rule removed successfully')
            setSelected({...option, rule: undefined})
            refetchRules()
        } catch {
            showMessage('Failed to remove rule', 'error')
        } finally {
            setIsDeleting(false)
        }
    }
  }

  const toOptions = (governorates: string[], districts: string[], areas: IArea[]): IOption[] => {
    const district_options = districts.map((r) => ({
            label: `${r} (District)`,
            scope: DeliveryChargeRuleScope.DISTRICT,
            name: r,
            district: r,
        }));

    const governorate_options = governorates.map((g) => ({
            label: `${g} (Governorate)`,
            scope: DeliveryChargeRuleScope.GOVERNORATE,
            name: g,
            governorate: g
        }));

    const areas_as_options = areas.map(area => {
        return {
            label: `${area.name_en} - ${area.name_ar}`,
            scope: DeliveryChargeRuleScope.AREA,
            name: area.name_en,
            area: area,
            district: area.district
        }
    })

    return [...district_options, ...governorate_options, ...areas_as_options].map(option => {return {...option, rule: getRule(option)}})
  }

  const fetch = async (query: string) => {
    if (query.length == 0) {
        return []
    }
    const matched_governorates = governorates.filter(item => item.toLowerCase().includes(query.toLowerCase()))
    const matched_districts = regions.map(item => item.district).filter(item => item.toLowerCase().includes(query.toLowerCase()))
    let matched_areas: IArea[] = [] 
    try {
        const response = await axios.get<IArea[]>(`${API_ENDPOINT}/v1/areas/?name=${query}`, config)
        matched_areas = response.data
    } catch {
        showMessage("Failed to load data", 'error')
    }

    return toOptions(matched_governorates, matched_districts, matched_areas)
  }

  const renderActions = (option: IOption) => {
    const rule = getRule(option);
    const showAdd = !rule

    console.log("option = " , option)

    return showAdd ? (
            !readonly && <Chip
                size="small"
                icon={<Add fontSize="small" />}
                disabled={!user_has_data_model_access(user, DataResources.DELIVERY_FEE_RULES, ResourceActionType.CREATE)}
                label={<span style={{fontSize: 12}}>Extra Charge</span>}
                onClick={(e:any) => {
                    e.stopPropagation();
                    setChargeInput("");
                    setDialogOpen(option);
                }}
                />
            ) : (
                <>
                    <Chip
                        size="small"
                        icon={readonly ? undefined : <Edit color='success' fontSize="small" />}
                        label={<span style={{fontSize: 12, color:'green'}}>Charge: {rule.delta_charge.isPositive() && '+'}{rule.delta_charge.toString()} $</span>}
                        onClick={(e: any) => {
                            e.stopPropagation();
                            setChargeInput(option.rule!.delta_charge.toString());
                            setDialogOpen(option);
                        }}
                        sx={{border: '1px solid green', background: alpha(green[100], 0.4), marginRight: 1}}
                        />

                    {!readonly && <Chip
                        size="small"
                        disabled={readonly}
                        icon={<Delete color='error' fontSize="small" />}
                        label={<span style={{fontSize: 12, color:'red'}}>Remove</span>}
                        onClick={(e: any) => {
                            e.stopPropagation();
                            setConfirmingDeleteFor(option)
                        }}
                        sx={{border: '1px solid red', background: alpha(red[100], 0.4)}}
                        />}

                 </>             
            )}
  return (
    <>
        <Box display={"flex"} gap={2} width={"100%"} justifyContent={"center"}>
    <Box width="100%" maxWidth={500}>
            <AutocompleteWithThrottle<IOption>
                        label={"Search governorate, district or city"}
                        onChange={(e, value)=>{
                            setSelected(value)
                            value ? setFocus({...value, delta_charge: Decimal(0)}) : setFocus(undefined)
                        }} 
                        fetch={fetch}
                        renderOption={(props, option) => {
                            const rule = getRule(option);
                            const showAdd = !rule;
                            return (
                                <Box
                                    component="li"
                                    {...props}
                                    display="flex"
                                    justifyContent="space-between"
                                    alignItems="center"
                                    width="100%"
                                    borderBottom="0.3px solid #aaa"
                                    px={1}
                                    py={0.5}
                                >
                                    <Typography sx={{ flex: 1 }} component="div">
                                        {option.label}
                                        {option.district && (
                                        <Typography variant="subtitle2" component="div">
                                            {option.district}
                                        </Typography>
                                        )}
                                    </Typography>
                                    {renderActions(option)}
                                </Box>
                                    );
                                }}
                        getOptionLabel={(option) => option.label} 
                        value={selected}
                    />
        </Box>
            {selected && <Box margin={1}>{renderActions(selected)}</Box>}
    </Box>
    
    {
        confirmingDeleteFor && <ConfirmAction 
                  message={`Are you sure you want to remove the extra charge for ${confirmingDeleteFor.name}`}
                  action={() => handleDelete(confirmingDeleteFor)}
                  isOpen={true}
                  handleClose={()=>setConfirmingDeleteFor(undefined)}>
                    <Alert severity="info"> Deleting a rule does not have a retroactive effect; old orders that used this rule will not be impacted.</Alert>
                  </ConfirmAction>
    }
      {/* Edit/Add Dialog */}
      {dialogOpen && !readonly && <Dialog open={true} onClose={() => setDialogOpen(undefined)} maxWidth={'sm'} fullWidth>
        <DialogTitle>
          {getRule(dialogOpen) ? "Edit Delivery Rule" : "Add Delivery Rule"} for {dialogOpen?.name}
        </DialogTitle>
        <DialogContent>
          <TextField
            label="Extra Charge Amount ($)"
            fullWidth
            margin="dense"
            type="number"
            value={chargeInput}
            onChange={(e) => setChargeInput(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(undefined)}>Cancel</Button>
          <Button variant="contained" onClick={()=>handleSave(dialogOpen)} disabled={isSaving}>
            Save
          </Button>
        </DialogActions>
      </Dialog>}
        </>
    
  );
};

export default DeliveryZoneAutocomplete;
